import { Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { IAppointment } from '../../API/GoMoto';
import {
    AppointmentDispatchTypes,
    useAppointmentDispatch,
    useAppointmentState,
    useAppointmentUpdateAndPersist,
} from '../../context/Appointment/appointment';
import { CTAButton } from '../../controls/ButtonBase';
import { useLocalization, useLocalizationExtension } from '../../context/Localization/localization';
import { Heading } from '../../controls/Heading/Heading';
import { KeyboardLayouts } from '../../controls/SimpleKeyboard/Layout';
import { nameof } from '../../helpers/nameof';
import { GMFormPhoneField, GMFormTextField } from '../../controls/TextField';
import { GMForm, GMFormWrapper } from '../../controls/Form';

export interface TowCompanyDetailsProps {
    onProceed: () => void;
}

export const TowCompanyDetails: React.FC<TowCompanyDetailsProps> = (props) => {
    const Localization = useLocalization();
    const { formatRequired } = useLocalizationExtension();
    const appointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const appointmentDispatch = useAppointmentDispatch();

    React.useEffect(() => {
        appointmentDispatch({
            type: AppointmentDispatchTypes.TowInProcess,
            payload: true,
        });
    }, [appointmentDispatch]);

    const initialValues: Pick<IAppointment, 'towInCompanyName' | 'towInDriverName' | 'towInPhone'> = {
        towInCompanyName: appointment.towInCompanyName ?? '',
        towInDriverName: appointment.towInDriverName ?? '',
        towInPhone: appointment.towInPhone ?? '',
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values) => {
                const updatedAppointment = {
                    ...appointment,
                    ...values,
                } as IAppointment;

                return setAppointment(updatedAppointment)
                    .then(props.onProceed);
            }}
            validationSchema={Yup.object()
                .shape({
                    [nameof<typeof initialValues>('towInCompanyName')]: Yup.string()
                        .required(formatRequired(Localization.TowCompanyName) as string)
                        .nullable(),
                })}
        >
            <GMForm
                heading={
                    <Heading
                        primaryHeading={Localization.LetsGetAcquainted}
                        secondaryHeading={Localization.PleaseEnterTowingCompanyInfo}
                        leftAlign
                    />
                }
                submitButton={(
                    <CTAButton
                        styleType={'primary'}
                        type={'submit'}
                        title={Localization.ClickToProceedToNextStep}
                        label={Localization.Proceed}
                        id={Localization.ClickToProceedToNextStep}
                        ariaLabel={Localization.ClickToProceedToNextStep}
                    />
                )}
                topDivider
            >
                <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                <GMFormTextField
                    name={nameof<typeof initialValues>('towInCompanyName')}
                    id={nameof<typeof initialValues>('towInCompanyName')}
                    title={Localization.TowCompanyName}
                    label={Localization.TowCompanyName}
                    ariaLabel={Localization.TowCompanyName}
                    keyboardLayout={KeyboardLayouts.AlphanumericShift}
                    autoFocus={initialValues.towInCompanyName === ''}
                    shiftAfterSpace
                    shiftAfterPeriod
                    shiftAfterAmpersand
                    required
                />
                <GMFormTextField
                    name={nameof<typeof initialValues>('towInDriverName')}
                    id={nameof<typeof initialValues>('towInDriverName')}
                    title={Localization.YourName}
                    label={Localization.YourName}
                    ariaLabel={Localization.YourName}
                    keyboardLayout={KeyboardLayouts.AlphanumericShift}
                    autoFocus={initialValues.towInCompanyName !== '' && initialValues.towInDriverName === ''}
                    shiftAfterSpace
                    shiftAfterPeriod
                    shiftAfterAmpersand
                />
                <GMFormPhoneField
                    name={nameof<typeof initialValues>('towInPhone')}
                    id={nameof<typeof initialValues>('towInPhone')}
                    title={Localization.YourPhoneNumber}
                    label={Localization.YourPhoneNumber}
                    ariaLabel={Localization.YourPhoneNumber}
                    autoFocus={initialValues.towInCompanyName !== '' && initialValues.towInDriverName !== '' && initialValues.towInPhone === ''}
                    required
                />
            </GMForm>
        </Formik>
    );
};
